/**
 * Formats a US phone number string into XXX-XXX-XXXX display format.
 * Automatically skips a leading US/Canada country code "1" when 11 digits
 * are present, so both "2038887887" and "12038887887" render as "203-888-7887".
 */
export const formatPhoneNumber = (value: string) => {
  const allDigits = Array.from(value).filter(char => /\d/.test(char));
  const digits =
    allDigits.length === 11 && allDigits[0] === '1'
      ? allDigits.slice(1)
      : allDigits;

  return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 10)]
    .filter(chunk => chunk.length > 0)
    .map(chunk => chunk.join(''))
    .join('-');
};

const digitsOnly = (value: string) => value.replace(/[^0-9]/g, '');

/**
 * US-specific normalizer: strips non-digits and removes the leading country
 * code "1" from 11-digit numbers so phone values stay in the 10-digit format
 * (XXX-XXX-XXXX) used consistently across the app.
 *
 * ⚠️ Only handles the US/Canada +1 country code. Do not use for international
 * phone numbers with other country codes.
 *
 * Examples:
 *   "+12038887887" → "2038887887"
 *   "12038887887"  → "2038887887"
 *   "2038887887"   → "2038887887"
 */
export const stripUSPhoneCountryCode = (value: string): string => {
  const digits = digitsOnly(value);
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
};

/**
 * Companion to stripUSPhoneCountryCode: ensures the US/Canada country code "1"
 * is present so the value is always in 11-digit format ready for API calls.
 *
 * ⚠️ Only handles the US/Canada +1 country code. Do not use for international
 * phone numbers with other country codes.
 *
 * Examples:
 *   "2038887887"   → "12038887887"
 *   "12038887887"  → "12038887887"
 *   "+12038887887" → "12038887887"
 */
export const addUSPhoneCountryCode = (value: string): string => {
  const digits = digitsOnly(value);
  return digits.length === 11 && digits.startsWith('1') ? digits : `1${digits}`;
};
